/** Client-side lifecycle of a voice call. */
export type VoiceCallStatus =
  | 'idle'
  | 'requesting'
  | 'connecting'
  | 'in-call'
  /** The media/signal connection dropped and LiveKit is retrying; the call is not over yet. */
  | 'reconnecting'
  | 'ending';

/** Published by the voice worker as the `lk.agent.state` participant attribute. */
export type VoiceAgentState = 'initializing' | 'idle' | 'listening' | 'thinking' | 'speaking';

/** `data` of `POST /voice/session`. */
export interface VoiceSession {
  url: string;
  token: string;
  room: string;
  conversationId: string;
  agentId: string;
  expiresIn: number;
}

export interface VoiceSessionResponse {
  success: boolean;
  message: string;
  data: VoiceSession;
  status_code: number;
}

export interface CreateVoiceSessionRequest {
  agentId: string;
  /** Continue an existing conversation by voice; omit to start a fresh one. */
  conversationId?: string;
}

/** One transcript segment, interim segments update in place until `isFinal`. */
export interface VoiceCaption {
  segmentId: string;
  who: 'agent' | 'user';
  text: string;
  isFinal: boolean;
}

export const VOICE_NOT_CONFIGURED_CODE = 'voice_not_configured';
export const LK_TRANSCRIPTION_TOPIC = 'lk.transcription';
export const LK_AGENT_STATE_ATTR = 'lk.agent.state';
export const LK_AGENT_IDENTITY_PREFIX = 'agent-';

export const VOICE_AGENT_STATES: readonly VoiceAgentState[] = [
  'initializing',
  'idle',
  'listening',
  'thinking',
  'speaking',
];

export const isVoiceAgentState = (value: unknown): value is VoiceAgentState =>
  typeof value === 'string' && (VOICE_AGENT_STATES as readonly string[]).includes(value);

/** English fallbacks for the status line until the UILM keys are registered. */
export const VOICE_STATUS_LABEL_DEFAULTS: Record<string, string> = {
  VOICE_CALL_STARTING: 'Starting call…',
  VOICE_CALL_CONNECTING: 'Connecting…',
  VOICE_CALL_LISTENING: 'Listening…',
  VOICE_CALL_THINKING: 'Thinking…',
  VOICE_CALL_SPEAKING: 'Speaking…',
  VOICE_CALL_ENDING: 'Ending call…',
  VOICE_CALL_RECONNECTING: 'Reconnecting…',
};

/** i18n key for the status line shown in the call UI. */
export const getVoiceStatusLabelKey = (
  status: VoiceCallStatus,
  agentState: VoiceAgentState
): string => {
  switch (status) {
    case 'requesting':
      return 'VOICE_CALL_STARTING';
    case 'connecting':
      return 'VOICE_CALL_CONNECTING';
    case 'ending':
      return 'VOICE_CALL_ENDING';
    case 'reconnecting':
      return 'VOICE_CALL_RECONNECTING';
    case 'in-call':
      switch (agentState) {
        case 'thinking':
          return 'VOICE_CALL_THINKING';
        case 'speaking':
          return 'VOICE_CALL_SPEAKING';
        case 'initializing':
          return 'VOICE_CALL_CONNECTING';
        default:
          return 'VOICE_CALL_LISTENING';
      }
    default:
      return 'VOICE_CALL_LISTENING';
  }
};
